// ─── Tenant Brand Kit ─────────────────────────────────────────────────────────
// Phase 7: fetches from /api/workspaces/me/branding on mount; localStorage is
// an offline cache that hydrates the initial render before the API responds.

import React from 'react'
import { apiClient } from '@/lib/api'

export interface TenantBrandKit {
  tenantId: string
  logoUrl: string | null           // PNG/SVG URL; null → text wordmark fallback
  logoAltText: string              // a11y alt text
  businessName: string             // fallback wordmark when logoUrl is null
  businessAddress: string          // PDF header right-column
  businessPhone: string            // PDF header
  businessWebsite: string          // PDF header
  primaryColor: string             // hex, e.g. "#F0B000"
  darkColor: string                // hex; auto-derived if not set
  accentColor: string | null       // hex; optional tertiary
  surfaceTint: string              // hex paper bg — default "#fbf9f8"
}

// ─── Workspace branding API shape (FOR-1130) ──────────────────────────────────

export interface WorkspaceBranding {
  businessName: string | null
  logoUrl: string | null
  brandPrimaryColor: string | null
  brandDarkColor: string | null
  brandSurfaceTint: string | null
  businessAddress: string | null
  businessPhone: string | null
  businessWebsite: string | null
}

// ─── Neutral defaults ─────────────────────────────────────────────────────────

/**
 * What a document shows before the workspace's own brand kit has loaded (or
 * when it has none), and the "reset" target. Deliberately neutral — never
 * another business's name, logo or contact details (DECISIONS §10/§14).
 * Colours match the server-side fallbacks in backend services/comms/brand.ts
 * (DEFAULT_INK / DEFAULT_ACCENT), so a preview and the sent PDF agree.
 */
export const DEFAULT_BRAND_KIT: TenantBrandKit = {
  tenantId: 'workspace',
  logoUrl: null,
  logoAltText: '',
  businessName: '',
  businessAddress: '',
  businessPhone: '',
  businessWebsite: '',
  primaryColor: '#B8893A',
  darkColor: '#1C1C1C',
  accentColor: null,
  surfaceTint: 'rgb(var(--fg-rgb))',
}

// ─── Ink (UX-032) ─────────────────────────────────────────────────────────────

/**
 * Customer documents print body text, headings and totals in the ink. It
 * stays near-black (DEFAULT_INK) unless the owner sets one explicitly, and
 * only a colour with 7:1 contrast on white is used (same rule as the server,
 * backend/src/lib/ink.ts). It is never derived from the main colour: a gold
 * accent used to print whole documents in mustard (#996E00).
 */
export const DEFAULT_INK = '#1C1C1C'
export const MIN_INK_CONTRAST = 7

export function contrastOnWhite(color: string | null | undefined): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec((color ?? '').trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  const lin = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  const l = 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2]
  return 1.05 / (l + 0.05)
}

/** The ink that will actually print for a stored colour. */
export function printableInk(color: string | null | undefined): string {
  const ratio = contrastOnWhite(color)
  return ratio != null && ratio >= MIN_INK_CONTRAST ? `#${color!.trim().replace(/^#/, '').toUpperCase()}` : DEFAULT_INK
}

// ─── deriveDarkColor ──────────────────────────────────────────────────────────

/**
 * Derive the document ink (body text and the title band) from the main colour:
 * a near-black with a hint of its hue. Lightness 14%, saturation at most 25%,
 * so CB gold #F0B000 gives a warm near-black, not the mustard #996E00 that a
 * plain "lightness 30%" clamp produced (UX-032).
 * Falls back to the input if parsing fails.
 */
export function deriveDarkColor(hex: string): string {
  // Strip leading '#'
  const clean = hex.replace(/^#/, '')
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return hex

  const r = parseInt(clean.slice(0, 2), 16) / 255
  const g = parseInt(clean.slice(2, 4), 16) / 255
  const b = parseInt(clean.slice(4, 6), 16) / 255

  // RGB → HSL
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min

  let h = 0
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6
    else if (max === g) h = (b - r) / delta + 2
    else h = (r - g) / delta + 4
    h = Math.round(h * 60)
    if (h < 0) h += 360
  }

  const l = (max + min) / 2
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

  const newL = 0.14
  const newS = Math.min(s, 0.25)

  // HSL → RGB
  const c = (1 - Math.abs(2 * newL - 1)) * newS
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = newL - c / 2

  let rp = 0, gp = 0, bp = 0
  if      (h < 60)  { rp = c; gp = x; bp = 0 }
  else if (h < 120) { rp = x; gp = c; bp = 0 }
  else if (h < 180) { rp = 0; gp = c; bp = x }
  else if (h < 240) { rp = 0; gp = x; bp = c }
  else if (h < 300) { rp = x; gp = 0; bp = c }
  else              { rp = c; gp = 0; bp = x }

  const toHex = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0')
  return `#${toHex(rp)}${toHex(gp)}${toHex(bp)}`
}

// ─── localStorage persistence ─────────────────────────────────────────────────

const STORAGE_KEY = 'tenant-brand-kit'

/**
 * Load the last brand kit this browser saw (an offline cache).
 * Returns the neutral DEFAULT_BRAND_KIT when nothing is stored or parsing fails.
 */
export function loadTenantBrandKit(): TenantBrandKit {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_BRAND_KIT
    const parsed = JSON.parse(raw) as TenantBrandKit
    // Merge with defaults so new fields added to the type are always present
    return { ...DEFAULT_BRAND_KIT, ...parsed }
  } catch {
    return DEFAULT_BRAND_KIT
  }
}

function cacheKit(kit: TenantBrandKit): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(kit)) } catch { /* storage full or blocked */ }
}

// ─── API mapping ──────────────────────────────────────────────────────────────

/**
 * Server branding → kit. The server is authoritative: a field the owner
 * cleared comes back null and stays blank, never a cached or default tenant's
 * value.
 */
export function kitFromBranding(branding: WorkspaceBranding): TenantBrandKit {
  const primary = branding.brandPrimaryColor ?? DEFAULT_BRAND_KIT.primaryColor
  const name = branding.businessName ?? ''
  return {
    ...DEFAULT_BRAND_KIT,
    businessName: name,
    logoAltText: name,
    logoUrl: branding.logoUrl ?? null,
    businessAddress: branding.businessAddress ?? '',
    businessPhone: branding.businessPhone ?? '',
    businessWebsite: branding.businessWebsite ?? '',
    primaryColor: primary,
    // Never derived from the main colour (UX-032): the owner's own ink when it
    // reads as body text, else near-black. Matches the server.
    darkColor: printableInk(branding.brandDarkColor),
    surfaceTint: branding.brandSurfaceTint ?? DEFAULT_BRAND_KIT.surfaceTint,
  }
}

// ─── Single in-flight fetch (deduplicate concurrent hook mounts) ──────────────

let _brandKitPromise: Promise<TenantBrandKit> | null = null

/**
 * GET the workspace branding. Throws on failure — callers that edit the
 * brand kit must not mistake a cached or default kit for the saved one (UX-003).
 */
export async function fetchWorkspaceBranding(): Promise<WorkspaceBranding> {
  const res = await apiClient.get<{ success: boolean; data: WorkspaceBranding }>('/api/workspaces/me/branding')
  const branding = res.data.data
  cacheKit(kitFromBranding(branding))
  return branding
}

function fetchBrandKitOnce(): Promise<TenantBrandKit> {
  if (!_brandKitPromise) {
    _brandKitPromise = fetchWorkspaceBranding()
      .then(kitFromBranding)
      .catch(() => {
        // Reset so the next useTenantBrandKit mount retries; documents keep
        // the last kit this browser saw meanwhile.
        _brandKitPromise = null
        return loadTenantBrandKit()
      })
  }
  return _brandKitPromise
}

// ─── Save / PATCH ─────────────────────────────────────────────────────────────

/** The branding fields the brand kit owns. Contact details live in Business profile. */
export type BrandingPatch = Partial<Pick<WorkspaceBranding,
  'logoUrl' | 'brandPrimaryColor' | 'brandDarkColor' | 'brandSurfaceTint' | 'businessAddress' | 'businessPhone' | 'businessWebsite'>>

/**
 * Only what changed between two kits. The logo URL in particular is sent only
 * when the owner removed or replaced it — uploads persist it server-side, and
 * re-sending an unchanged relative URL ("/uploads/logos/…") is what made every
 * brand-kit save fail (UX-009).
 */
export function buildBrandingPatch(next: TenantBrandKit, prev: TenantBrandKit): BrandingPatch {
  const patch: BrandingPatch = {}
  if (next.logoUrl !== prev.logoUrl) patch.logoUrl = next.logoUrl
  if (next.primaryColor !== prev.primaryColor) patch.brandPrimaryColor = next.primaryColor
  if (next.darkColor !== prev.darkColor) patch.brandDarkColor = next.darkColor
  if (next.surfaceTint !== prev.surfaceTint) patch.brandSurfaceTint = next.surfaceTint
  if (next.businessAddress !== prev.businessAddress) patch.businessAddress = next.businessAddress || null
  if (next.businessPhone !== prev.businessPhone) patch.businessPhone = next.businessPhone || null
  if (next.businessWebsite !== prev.businessWebsite) patch.businessWebsite = next.businessWebsite || null
  return patch
}

/**
 * PATCH /api/workspaces/me/branding with exactly `patch`, then refresh every
 * useTenantBrandKit() subscriber. Server rejections are thrown for the caller
 * to show; nothing is written locally unless the server accepted it.
 */
export async function patchWorkspaceBranding(patch: BrandingPatch): Promise<TenantBrandKit> {
  const res = await apiClient.patch<{ success: boolean; data: WorkspaceBranding }>('/api/workspaces/me/branding', patch)
  const saved = kitFromBranding(res.data.data)
  cacheKit(saved)
  _brandKitPromise = null
  window.dispatchEvent(new CustomEvent('tenant-brand-kit-updated', { detail: saved }))
  return saved
}

/**
 * Persist a whole kit. Sends only the fields that differ from `previous`
 * (default: the last kit this browser saw).
 */
export async function saveTenantBrandKit(kit: TenantBrandKit, previous: TenantBrandKit = loadTenantBrandKit()): Promise<TenantBrandKit> {
  return patchWorkspaceBranding(buildBrandingPatch(kit, previous))
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current workspace brand kit, reactive to saveTenantBrandKit() calls.
 * Hydrates from localStorage on first render; reconciles with the API response
 * asynchronously (single request per page load shared across all hook instances).
 */
export function useTenantBrandKit(): TenantBrandKit {
  const [kit, setKit] = React.useState<TenantBrandKit>(() => loadTenantBrandKit())

  // Fetch from API once per page load; update state when resolved
  React.useEffect(() => {
    let active = true
    fetchBrandKitOnce().then((fetched) => {
      if (active) setKit(fetched)
    })
    return () => { active = false }
  }, [])

  // Subscribe to updates from saveTenantBrandKit()
  React.useEffect(() => {
    const handler = (e: Event) => {
      const custom = e as CustomEvent<TenantBrandKit>
      if (custom.detail) setKit(custom.detail)
    }
    window.addEventListener('tenant-brand-kit-updated', handler)
    return () => window.removeEventListener('tenant-brand-kit-updated', handler)
  }, [])

  return kit
}
